import React, { ReactNode, useState, useMemo, useEffect, useRef, useCallback} from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Text from '../text';
import PrimaryButton from '../button/PrimaryButton';
import Button from '../button';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../theme/muiTheme';
import Calibration from '../calibration';
import SecondaryButton from '../button/SecondaryButton';
import Test from '../test';
import CollapsibleTable from '../collapsible-table';
import CircularProgress from '@mui/material/CircularProgress';
import ApiService from '../../utils/services/ApiService';
import CancelConfirmationDialog from '../dialog/Cancel';
import TableSettings, { TABLE_SETTINGS_TABS } from '../menu/table-settings';
import Tooltip from '@mui/material/Tooltip';
import TableSettingsDialog from '../dialog/TableSettings';
import {
	getTestResults,
	initDB,
	saveTestResults,
} from '../../utils/services/idbService';
import { useSelector } from 'react-redux';
import { IReduxState } from 'utils/models/redux-states';
import { User } from 'utils/models/user';
import { RiskScoreVO } from 'utils/models/risk-score';
import { getAlarmsFromCache } from 'utils/functions/getAlarmsFromCache';
import { SiteAlarmSetting } from 'utils/models/location-alarms';
import { convertDataLocation, mapCacheDescriptorsToSimpleDescriptors } from 'utils/functions/convertSIteDataintoLocations';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { TestSelection } from '../../types/testSelection';
import { useConnection } from 'utils/helper/ConnectionContext';
import SiteNameEditor from './SiteNameEditor';
import StarterWelcome from './StarterWelcome';
import MakeItYoursPanel from './MakeItYoursPanel';
import {
	OPEN_SITES_AND_GROUPS_EVENT,
	dismissStarterPanel,
	isDefaultLocationName,
	isDefaultSiteName,
	isStarterPanelDismissed,
} from 'utils/constants/starterSite';

interface SiteProps {
	children: ReactNode;
	addStyle?: string;
	siteName?: string;
	siteId: number;
	dataPoints?: any[];
	isLoading?: boolean;
	userId?: number;
	testKitsUnits?: any;
	isOwner?: boolean;
	isOffline?: boolean;
	tableColumnSettingsAllVOs?: TableColumnSettingsAllVO[];
	onTestDone: (siteId: number, samplePointId?: number) => void;
	samplePoint?: any;
	virtuosoRef: React.RefObject<any>;
	index: number;
	onRunTestsClicked: (index: number) => void;
	dialogOpen: boolean;
	setDialogOpen: (open: boolean) => void;
	currentTab: number;
	setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
}

const Site = ({
	children,
	addStyle,
	siteId,
	siteName,
	dataPoints,
	isLoading,
	userId,
	isOwner,
	tableColumnSettingsAllVOs,
	isOffline,
	testKitsUnits,
	onTestDone,
	samplePoint,
	virtuosoRef,
	index,
	onRunTestsClicked,
	dialogOpen,
	setDialogOpen,
	currentTab,
	setCurrentTab,
}: SiteProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	// Starter site (PWA-3011): the welcome state and "Make it yours" panel.
	const starterUserKey = localStorage.getItem('currentUser') ?? 'unknown';
	const [renameRequest, setRenameRequest] = useState(0);
	const [starterPanelDismissed, setStarterPanelDismissed] = useState(() =>
		isStarterPanelDismissed(starterUserKey, siteId)
	);
	const openSitesAndGroups = () => window.dispatchEvent(new Event(OPEN_SITES_AND_GROUPS_EVENT));
	const starterGroupName = useSelector((state: IReduxState) =>
		state.groups?.groups.find((group) => group.sites?.some((site) => site.id === siteId))?.name
	);

	const [testComponents, setTestComponents] = useState<number[]>([1]);
	const [viewMode, setViewMode] = useState<'latest' | 'all'>('latest');
	const [loading, setIsLoading] = useState(false);
	const [allDataPoints, setAllDataPoints] = useState<any[]>([]);
	const [additionalLoading, setAdditionalLoading] = useState(false);
	const reduxState = useSelector((state: IReduxState) => state.user);
	const userData = reduxState?.userInformation;
	const reduxLocations = useSelector((state: any) => state.updateLocation);
	const [isTestLoading, setIsTestLoading] = useState(false);

	const { isConnected, isPBM, serialNumber, pbmSerialNumber, newFirmwareNumber } = useConnection();
	const firmwareNumber = !isPBM ? localStorage.getItem('analyzerFirmwareVersion') ?? 'N/A' : 'N/A';
	const eventLogMessage = useMemo(() =>
		!isConnected ? `No device connected.` 
			: !isPBM ? `Firmware Version: ${firmwareNumber} `
			: `PM Serial: ${serialNumber}, PBM ID: ${pbmSerialNumber}`,
	[isConnected, isPBM, serialNumber, pbmSerialNumber, firmwareNumber]);

	const handleSetViewMode = useCallback((mode: 'latest' | 'all') => {
		setViewMode(mode);
		addEventLog(`View ${mode === 'latest' ? 'Latest' : 'All'} Test Results Success`, "Change View Mode", siteId);
	}, [siteId]);
	
  	const transformDataPoints = (datapoints: any, onlyVisible: boolean = true) => {
		let units = {...(testKitsUnits || {})}
		if (!datapoints) {
			return [];
		}

		return datapoints.flatMap((dp: any) => {
			if (!dp.dataPointAllVOs) return [];

			return dp.dataPointAllVOs.flatMap((dataPoint: any) => {
				if (!dataPoint.valueVOs) return [];
				return dataPoint.valueVOs
					.filter((valueVO: any) => !onlyVisible || valueVO?.visible)
					.map((valueVO: any) => {
						let matchingDescriptor: any = {}
						if (units?.[dataPoint.testKitId]?.[valueVO.outputId]) {
							matchingDescriptor = units[dataPoint.testKitId][valueVO.outputId];
						}

						return {
							displayName: valueVO.displayName,
							id: dataPoint.id,
							note: dataPoint.note,
							operator: dataPoint.operator,
							outputAlarm: valueVO.outputAlarm,
							outputName: valueVO.outputId,
							outputType: 'Input',
							outputId: valueVO.outputId,
							outputUnit: matchingDescriptor ? matchingDescriptor.unit : " ",
							outputValue: valueVO.outputValue,
							samplePointId: dp.id,
							samplePointName: dp.name,
							siteId: siteId,
							siteName: dp.name,
							testkitId: dataPoint.testKitId,
							testkitName: dataPoint.testKitName,
							timestamp: dataPoint.timestamp,
							visible: valueVO.visible,
						};
					});
			});
		});
	};

	const transformedDataPoints = transformDataPoints(dataPoints);

	const filteredDataPoints = transformedDataPoints;

	const unfilteredDataPoints = useMemo(() => {
		return transformDataPoints(dataPoints, false);
	}, [dataPoints, testKitsUnits, siteId]);

	const displayedDataPoints = useMemo(() => {
		return filteredDataPoints;
	}, [viewMode, filteredDataPoints, allDataPoints]);

	const [locations, setLocations] = useState<
		{
			id: number;
			name: string;
			testKitDescriptors: {
				testKitName: string;
				id: number;
				testKitInputDescriptorVOs: any[];
				testKitOutputDescriptorVOs: any[];
				riskScore: RiskScoreVO;
			}[];
		}[]
	>([]);
	// The panel's shortcuts rename and add things, so only the owner sees it, and not while an admin views another user.
	const showStarterPanel =
		!!isOwner &&
		!reduxState?.viewedUserInfo &&
		!starterPanelDismissed &&
		(isDefaultSiteName(siteName) || locations.some((location) => isDefaultLocationName(location.name)));
	const [locationAlarms, setLocationAlarms] = useState<SiteAlarmSetting[]>([]);
	const [missingFields, setMissingFields] = useState<string[]>([]);
	const [isCancelTestDialogOpen, setIsCancelTestDialogOpen] = useState(false);
	const [testSelectionData, setTestSelectionData] = useState<TestSelection[]>([]);

	const handleCancelTest = () => {
		handleTestRun();
		closeCancelTestDialog();
		addEventLog("Run Test Cancelled", "Tests", siteId, undefined, eventLogMessage);
	};

	const openCacelTestDialog = () => {
		setIsCancelTestDialogOpen(true);
	};

	const closeCancelTestDialog = () => {
		setIsCancelTestDialogOpen(false);
	};

	const [isOnline, setIsOnline] = useState(navigator.onLine);

	useEffect(() => {
		const updateOnlineStatus = () => setIsOnline(navigator.onLine);

		window.addEventListener('online', updateOnlineStatus);
		window.addEventListener('offline', updateOnlineStatus);

		return () => {
			window.removeEventListener('online', updateOnlineStatus);
			window.removeEventListener('offline', updateOnlineStatus);
		};
	}, []);

	const handleTestRun = async () => {
		if (!isExpanded) {
			onRunTestsClicked(index);
		}

		// Toggle states
		setIsExpanded((prev) => !prev);

		// If isExpanded is true, set initial values and return
		if (isExpanded) {
			setTestComponents([1]);
			setMissingFields([]);
			return;
		}

		setAdditionalLoading(true);

		try {
			let response;

			const username = localStorage.getItem('currentUser');
			if (!username) {
				throw new Error('No current user found.');
			}

			let alarms = await getAlarmsFromCache(siteId);
			setLocationAlarms(alarms);

			if (isOnline) {
				response = await ApiService.getLocationBySiteID(siteId);
				if (Array.isArray(response.data)) {
					const extractedLocations = response.data
						.filter(
							(location) =>
								location.testKitDescriptors?.some(
									(tk: { testKitName: any; id: any }) =>
										tk.testKitName && tk.id
								)
						)
						.map((location) => ({
							id: location.id,
							name: location.name,
							testKitDescriptors: location.testKitDescriptors
								.filter(
									(tk: { testKitName: any; id: any }) =>
										tk.testKitName && tk.id
								)
								.map(
									(tk: {
										riskScoreVO: any;
										testKitName: any;
										id: any;
										testKitInputDescriptorVOs: any;
										testKitOutputDescriptorVOs: any;
										outPutAlarmsVos: any;
									}) => {
										return {
											testKitName: tk.testKitName,
											id: tk.id,
											outPutAlarmsVos:
												location.testKitDescriptors[0]
													.outPutAlarmsVos[0],
											testKitInputDescriptorVOs:
												tk.testKitInputDescriptorVOs,
											testKitOutputDescriptorVOs:
												tk.testKitOutputDescriptorVOs,
											riskScoreVO: tk.riskScoreVO,
										};
									}
								),
						}));
					setAdditionalLoading(false);
					setLocations(extractedLocations);
					
					await saveTestResults(
						`location_${siteId}`,
						response.data,
						username
					);

				}
			} else {
				// Handle the case when offline
				const cachedData = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				const defaultTestKits = await getTestResults(`defaultTestKits`, localStorage.getItem('currentUser') ?? 'unknown');
				const SiteIndex = cachedData.findIndex((site: any)=> site.id === siteId)
				const offlineData = convertDataLocation(cachedData[SiteIndex],defaultTestKits)
				response = await getTestResults(`location_${siteId}`, username);
				if (Array.isArray(response)) {
					setLocations(response);
				} else if (Array.isArray(offlineData)) {
					const extractedLocations = offlineData
						.filter(
							(location) =>
								location.testKitDescriptors?.some(
									(tk: { testKitName: any; id: any }) =>
										tk.testKitName && tk.id
								)
						)
						.map((location) => ({
							id: location.id,
							name: location.name,
							testKitDescriptors: location.testKitDescriptors
								.filter(
									(tk: { testKitName: any; id: any }) =>
										tk?.testKitName && tk?.id
								)
								.map(
									(tk: {
										riskScoreVO: any;
										testKitName: any;
										id: any;
										testKitInputDescriptorVOs: any;
										testKitOutputDescriptorVOs: any;
										outPutAlarmsVos: any;
									}) => {
										return {
											testKitName: tk?.testKitName,
											id: tk?.id,
											outPutAlarmsVos:
												location?.testKitDescriptors[0]
													?.outPutAlarmsVos[0],
											testKitInputDescriptorVOs:
												tk?.testKitInputDescriptorVOs,
											testKitOutputDescriptorVOs:
												tk?.testKitOutputDescriptorVOs,
											riskScoreVO: tk?.riskScoreVO,
										};
									}
								),
						}));
					setAdditionalLoading(false);
					setLocations(extractedLocations || []);
					//remove comment if we are adding to the monolith/sync
					// await saveTestResults(
					// 	`location_${siteId}`,
					// 	response.data,
					// 	username
					// );
				}
				setAdditionalLoading(false);
			}

			setAdditionalLoading(false);
			addEventLog("Run Test Opened", "Tests", siteId, undefined, eventLogMessage);

		} catch (error) {
			console.error('Error fetching data:', error);
			setAdditionalLoading(false);
		}
	};

	const handleRemoveTestClick = useCallback((indexToRemove: number) => {
		if (testComponents.length === 1) {
			// If this is the last test, clear selection, collapse and reset to Test 1
			setTestSelectionData([]);
			setIsExpanded(false);
			setTestComponents([1]);
		} else {
			setTestSelectionData((prev) => prev.filter((data) => data.index !== indexToRemove));
			setTestComponents((prevIndices) =>
				prevIndices.filter((index) => index !== indexToRemove)
			);
		}
	}, [testComponents.length]);

	const handleAddTestClick = useCallback(() => {
		// Find the next test number by finding the highest test number and adding 1.
		const nextIndex = Math.max(1, ...testComponents) + 1;
		setTestComponents((prevIndices) => [...prevIndices, nextIndex]);
		setTestSelectionData((prev) => [...prev, {
			index: nextIndex,
			location: '',
			testKit: '',
			outputValues: {},
		}]);
		addEventLog("Add Another Test", "Tests", siteId, undefined, eventLogMessage);
	}, [testComponents, siteId, eventLogMessage]);
	const [calibrationData, setCalibrationData] = useState<{
		[inputId: string]: number;
	}>({});

	const handleCalibrationChange = useCallback((
		inputId: string,
		value: number,
		updatedMissingFields: string[]
	) => {
		setCalibrationData((prevData) => ({ ...prevData, [inputId]: value }));
		setMissingFields(updatedMissingFields);
	}, []);
	const handleTestDone = async (testIndex: number, samplePointId?: number) => {
		handleRemoveTestClick(testIndex);
		onTestDone(siteId, samplePointId);
		const username = localStorage.getItem('currentUser') ?? 'unknown';
		const data = await getTestResults(`dataPoints_all_${siteId}`, username);
		setAllDataPoints(data || []);
		setTestSelectionData((prev) => prev.filter((data) => data.index !== testIndex));
		await fetchTableColumns();
	};

	const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseUserMenu = () => {
		setAnchorEl(null);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleSelectionComplete = useCallback((selectionData: TestSelection) => {
		setTestSelectionData((prevData) => {

			if (prevData === null || prevData.length === 0) {
				return [selectionData];
			}

			const existingIndex = prevData.findIndex(
				(item) => item.index === selectionData.index
			);
			if (existingIndex === -1) {
				const updatedData = [...prevData, selectionData];
				return updatedData;
			} else {
				const updatedData = [...prevData];
				updatedData[existingIndex] = selectionData;
				return updatedData;
			}
		})
	}, []);

	const handleTabSelected = (tabIndex: number) => {
		handleMenuClose();
		setCurrentTab(tabIndex);
		setDialogOpen(true);
	};

	useEffect(() => {
		const initializeDatabase = async () => {
			await initDB();
		};

		initializeDatabase();
	}, []);

	const fetchTableColumns = async () => {
		try {
			const username = localStorage.getItem('currentUser');
			if (!username) {
				throw new Error('No current user found.');
			}

			let columnsDataCached = await getTestResults(
				`columns_data_${siteId}`,
				username
			);

			if (
				isOnline &&
				(!columnsDataCached ||
					typeof columnsDataCached !== 'object' ||
					!columnsDataCached.hasOwnProperty('0'))
			) {
				setIsLoading(true);
				const response = await ApiService.getTableColumns(siteId);
				setIsLoading(false);

				await saveTestResults(
					`columns_data_${siteId}`,
					response.data,
					username
				);
			}
		} catch (error) {
			console.error('Error fetching table columns:', error);
		} finally {
			setIsLoading(false);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		updateLocations();
	}, [reduxLocations]);

	const getUserData = async () => {
		const username = localStorage.getItem('currentUser') ?? 'unknown';
		if (navigator.onLine) {
			if (userData !== undefined) {
				await saveTestResults('userData_', [userData], username);
			}
		}
	};

	//This gets called when scrolling on the main page for each site. Uses cache.
	const updateLocations = async () => {
		try {
			const allData = await getTestResults(
				`allSites`,
				localStorage.getItem('currentUser') ?? 'unknown'
			);
		
			const site = allData.find((site: {id: number}) => site.id === siteId);
		
			const cachedKits = await getTestResults(
				`defaultTestKits`,
				localStorage.getItem('currentUser') ?? 'unknown'
			);

			let cacheLocations = convertDataLocation(site, cachedKits);

			setLocations(cacheLocations);

		} catch (error) {
			console.log("Error fetching location details in site", error);
		}
	};

	useEffect(() => {
		getUserData();
	}, []);

	return (
		<ThemeProvider theme={theme}>
			<div className="flex justify-center " style={{}}>
				<div
					data-site-id={siteId}
					style={{
						boxShadow: '0 3px 4px 0 rgba(0,0,0,0.08)',
					}}
					className={`w-[1128px] max-w-full min-w-0 rounded-[4px] border-[1px] border-[#9EACB4] overflow-hidden ${
						isExpanded ? 'bg-tertiary-grey-2' : ''
					} ${addStyle}`}
				>
					<div style={{minWidth: 'fit-content' }}>
						<AppBar
							position="static"
							elevation={0}
							sx={{
								bgcolor: isExpanded
									? (theme) => theme.palette.tertiaryGrey2.main
									: (theme) =>
											theme.palette.tertiaryLightBlue.main,
								borderBottom: isExpanded
									? 'none'
									: '1px solid #9EACB4',
								borderTopLeftRadius: '4px',
								borderTopRightRadius: '4px',
							}}
						>
							<Toolbar className="flex items-center px-0 h-[68px]">
								<SiteNameEditor
									siteId={siteId}
									siteName={siteName}
									canRename={isOwner}
									editRequest={renameRequest}
								/>

								<Box sx={{ flexGrow: 1 }}></Box>

								<Text
									addStyle={`!font-inter !mr-4 !text-[14px] !font-semibold text-center !leading-6 !tracking-wider font-bold text-primary-grey ${
										viewMode === 'all'
											? 'your-selected-text-style'
											: 'your-unselected-text-style'
									}`}
								>
									{viewMode === 'all'
										? 'VIEW ALL'
										: 'VIEW LATEST'}
								</Text>

								<Button
									style={{
										maxWidth: '48px',
										maxHeight: '48px',
										minWidth: '48px',
										minHeight: '48px',
										border: '1px solid #9EACB4',
										borderRadius: '2px 0px 0px 2px',
									}}
									addStyle={`!h-[40px] w-[40px] ${
										viewMode === 'latest'
											? '!bg-tertiary-grey'
											: '!bg-white'
									}`}
									onClick={() => handleSetViewMode('latest')}
								>
									<img
										src={`/assets/icons/${
											viewMode === 'latest'
												? 'Latest-white'
												: 'Latest'
										}.svg`}
										alt="Latest"
										className="w-5 h-5 md:block hidden"
									/>
								</Button>

								<Button
									style={{
										maxWidth: '48px',
										maxHeight: '48px',
										minWidth: '48px',
										minHeight: '48px',
										border: '1px solid #9EACB4',
										borderRadius: '0px 2px 2px 0px',
									}}
									addStyle={`!h-[40px] w-[40px] ${
										viewMode === 'all'
											? '!bg-tertiary-grey'
											: '!bg-white'
									}`}
									onClick={() => handleSetViewMode('all')}
								>
									<img
										src={`/assets/icons/${
											viewMode === 'all'
												? 'ViewAll-white'
												: 'ViewAll'
										}.svg`}
										alt="View All"
										className="w-5 h-5 md:block hidden"
									/>
								</Button>
								<Tooltip title="Export">
									<Button
										style={{
											maxWidth: '48px',
											maxHeight: '48px',
											minWidth: '48px',
											minHeight: '48px',
											marginLeft: '10px',
										}}
										addStyle="flex items-center bg-transparent hover:bg-gray-100 !font-jost text-[14px] font-normal leading-[18px] !text-primary-grey !ml-2"
										onClick={() => {
											setDialogOpen(true);
											setCurrentTab(TABLE_SETTINGS_TABS.indexOf('Export results'));
										}}
									>
										<img
											src="/assets/icons/Export2.svg"
											alt="Export Icon"
											className="w-[25px] h-[25px] md:block hidden !opacity-[0.55]"
										/>
									</Button>
								</Tooltip>
								<Tooltip title="Site setup">
									<IconButton
										onClick={handleOpenUserMenu}
										sx={{
											mr: '8px',
											padding: '14px',
											hover: 'bg-gray-100',
										}}
									>
										<img
											src="assets/icons/Settings.svg"
											alt="Settings Icon"
											className="w-[25px] h-[25px] md:block hidden opacity-90"
										/>
									</IconButton>
								</Tooltip>

								<TableSettings
									anchorEl={anchorEl}
									onClose={handleCloseUserMenu}
									onTabSelected={handleTabSelected}
									siteId={siteId}
								/>
								<TableSettingsDialog
									open={dialogOpen}
									currentTab={currentTab}
									onClose={() => {
										setDialogOpen(false);
										addEventLog(`Site Table Settings Closed`, "Site Management", siteId, undefined);
									}}
									setCurrentTab={setCurrentTab}
									siteName={siteName}
									siteId={siteId}
									isOwner={isOwner}
									dataPoints={unfilteredDataPoints}
									tableColumnSettingsAllVOs={tableColumnSettingsAllVOs}
									viewMode={viewMode}
								/>
								<CancelConfirmationDialog
									open={isCancelTestDialogOpen}
									onClose={closeCancelTestDialog}
									onConfirm={handleCancelTest}
								/>
								{isExpanded ? (
									<SecondaryButton
										onClick={openCacelTestDialog}
										addStyle="!w-[138px] !h-[48px]"
									>
										CANCEL TESTS
									</SecondaryButton>
								) : (
									<PrimaryButton
										onClick={handleTestRun}
										addStyle="!w-[138px] !h-[48px] !tracking-[0.46px] run-tests"
									>
										RUN TESTS
									</PrimaryButton>
								)}
							</Toolbar>
						</AppBar>
					</div>
					<div
						data-site-id={siteId}
						style={{ 
							overflow: 'auto', 
							width: '100%', 
							scrollbarColor: '#868683 transparent',
							scrollbarWidth: 'thin' 
						}}
						className={`site-scroll-container  ${isExpanded ? 'bg-tertiary-grey-2' : ''} ${addStyle}`}
					>
						<div
							style={{ overflow: 'auto', width: '100%' }}
						>
							{/* Collapse div */}
							<Collapse in={isExpanded}>
								<Calibration
									locations={locations}
									onInputChange={handleCalibrationChange}
									missingFields={missingFields}
									selectionData={testSelectionData ?? null}
									siteId={siteId}
								/>

								{testComponents.map((index) => (
									<Test
										key={`test-${index}`}
										onAddTestClick={handleAddTestClick}
										onRemoveTestClick={() =>
											handleRemoveTestClick(index)
										}
										index={index}
										canRemove={true}
										locations={locations}
										alarmSettingsPerLocation={locationAlarms}
										calibrationData={calibrationData}
										userId={userId}
										siteId={siteId}
										siteName={siteName}
										onTestDone={(samplePointId) => handleTestDone(index, samplePointId)}
										onMissingFieldsUpdate={setMissingFields}
										onSelectionComplete={
											handleSelectionComplete
										}
										isTestLoading={isTestLoading}
										setIsTestLoading={setIsTestLoading}
										shouldClear={!isExpanded}
									/>
								))}
							</Collapse>

							{/* Add any other content you want here... */}
							{loading || isLoading ? (
								<div className="flex justify-center items-center h-[200px]">
									<CircularProgress />
								</div>
							) : displayedDataPoints && displayedDataPoints.length > 0 ? (
								<>
									{showStarterPanel && (
										<MakeItYoursPanel
											siteName={siteName}
											onRenameSite={() => setRenameRequest((n) => n + 1)}
											onOpenLocations={() => {
												setCurrentTab(TABLE_SETTINGS_TABS.indexOf('Sample locations'));
												setDialogOpen(true);
											}}
											onAddSite={openSitesAndGroups}
											onDismiss={() => {
												dismissStarterPanel(starterUserKey, siteId);
												setStarterPanelDismissed(true);
												addEventLog('Starter Panel Dismissed', 'Site Management', siteId);
											}}
										/>
									)}
									<CollapsibleTable
										viewMode={viewMode}
										dataPoints={displayedDataPoints}
										tableColumnSettingsAllVOs={tableColumnSettingsAllVOs}
										isOwner={isOwner}
										siteId={siteId}
										isLoading={isLoading}
										userId={userId}
									/>
								</>
							) : isDefaultSiteName(siteName) ? (
								<StarterWelcome
									siteName={siteName}
									groupName={starterGroupName}
									locationName={locations[0]?.name}
									onSetUpOwnSites={openSitesAndGroups}
								/>
							) : (
								<div className="relative flex flex-col justify-center items-center h-[200px] bg-white">
									<div
										style={{ //right border, fixes a half pixel offset for empty tables
											position: 'absolute',
											top: 0,
											bottom: 0,
											right: '0.5px',
											width: '1px',
										}}
									/>
									<Text addStyle="!font-jost !text-[16px] !text-primary-grey !leading-[32px]">
										<span
											style={{
												display: 'inline-block',
												paddingBottom: '10px',
												fontWeight: '500',
												fontSize: '16px',
											}}
										>
											No results yet. To start testing:
										</span>
										<ol>
											<li>
												1.{' '}
												<span
													style={{
														fontWeight: '500',
													}}
												>
													SITE SETUP
												</span>{' '}
												<img
													src="assets/icons/Settings.svg"
													alt="Settings Icon"
													className="w-[18px] h-[18px] md:inline-block !mt-[-4px] hidden opacity-80"
												/>{' '}
												→ Sample locations
											</li>
											<li>2. Run a test</li>
										</ol>
									</Text>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</ThemeProvider>
	);
};

export default Site;
